import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Copy, Check, Volume2, Loader2, Square } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { WordStudy } from "@/components/WordStudy";
import { ShareMenu } from "@/components/ShareMenu";
import { ScripturesReferenced } from "@/components/ScripturesReferenced";

function buildPlainText(session) {
  const lines = [session.intro, ""];
  session.decrees.forEach((d, i) => {
    lines.push(`${i + 1}. ${d.text}`);
    lines.push(`   (${d.reference})`);
    lines.push("");
  });
  lines.push("— It Is Established");
  return lines.join("\n");
}

export function DecreeList({ session }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);
  const [playing, setPlaying] = useState(null); // index | 'all' | null
  const [activeIdx, setActiveIdx] = useState(null); // decree currently being read
  const [loadingIdx, setLoadingIdx] = useState(null);
  const audioRef = useRef(null); // ONE reusable element (unlocked by first user gesture)
  const queueRef = useRef({ active: false, idx: 0, fails: 0 });

  useEffect(() => () => stop(), []); // cleanup on unmount

  // Create a single Audio element once; reusing it (swapping .src) avoids the
  // mobile-Safari autoplay block that stops chained playback after decree 1.
  function getAudio() {
    if (!audioRef.current) audioRef.current = new Audio();
    return audioRef.current;
  }

  const fetchAudio = async (text) => {
    const { data } = await api.post("/tts", { text });
    return `data:audio/mp3;base64,${data.audio}`;
  };

  function stop() {
    const a = audioRef.current;
    if (a) { a.pause(); a.onended = null; a.onerror = null; }
    queueRef.current.active = false;
    setPlaying(null);
    setActiveIdx(null);
    setLoadingIdx(null);
  }

  const guardListen = () => {
    if (user) return true;
    toast.info("Sign in to listen — create a free account to hear your decrees read aloud.");
    navigate("/auth");
    return false;
  };

  const playOne = async (idx) => {
    if (!guardListen()) return;
    if (playing === idx) { stop(); return; }
    stop();
    setLoadingIdx(idx);
    try {
      const src = await fetchAudio(session.decrees[idx].text);
      const a = getAudio();
      a.onended = () => { setPlaying(null); setActiveIdx(null); };
      a.src = src;
      await a.play();
      setPlaying(idx);
      setActiveIdx(idx);
    } catch (e) {
      toast.error("Could not play audio. Please try again.");
    } finally {
      setLoadingIdx(null);
    }
  };

  const playAll = async () => {
    if (!guardListen()) return;
    if (playing === "all") { stop(); return; }
    stop();
    queueRef.current = { active: true, idx: 0, fails: 0 };
    setPlaying("all");
    const a = getAudio();
    const step = async () => {
      if (!queueRef.current.active) return;
      const i = queueRef.current.idx;
      if (i >= session.decrees.length) { stop(); return; }
      setActiveIdx(i);
      try {
        const src = await fetchAudio(session.decrees[i].text);
        if (!queueRef.current.active) return;
        a.onended = () => { queueRef.current.idx += 1; step(); };
        a.onerror = () => { if (queueRef.current.active) { queueRef.current.idx += 1; step(); } };
        a.src = src;
        await a.play();
        queueRef.current.fails = 0;
      } catch (e) {
        // Skip a single failed/blocked decree instead of ending the whole reading.
        queueRef.current.fails += 1;
        if (queueRef.current.fails >= 3) { stop(); toast.error("Could not play audio. Please try again."); return; }
        queueRef.current.idx += 1;
        step();
      }
    };
    step();
  };

  const copyAll = async () => {
    await navigator.clipboard.writeText(buildPlainText(session));
    setCopied(true);
    toast.success("All decrees copied to clipboard");
    setTimeout(() => setCopied(false), 2000);
  };

  const copyOne = async (d, i) => {
    await navigator.clipboard.writeText(`${d.text}\n(${d.reference})`);
    toast.success(`Decree ${i + 1} copied`);
  };

  const setPayload = {
    kind: "set",
    topic: session.topic,
    intro: session.intro,
    decrees: session.decrees.map((d) => ({ text: d.text, reference: d.reference })),
  };

  return (
    <div data-testid="decree-results" className="animate-fade-up">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
        <p className="font-decree text-xl sm:text-2xl text-[#4A4744] leading-relaxed max-w-2xl">
          {session.intro}
        </p>
        <div className="flex items-center gap-2 shrink-0 flex-wrap">
          <button
            data-testid="listen-all-button"
            onClick={playAll}
            className="flex items-center gap-2 px-4 py-2.5 text-sm border border-[#D9D4CC] text-[#2C2A29] hover:bg-[#EAE6DF] transition-colors duration-300 rounded-sm"
          >
            {playing === "all" ? <Square className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
            {!user ? "Sign in to listen" : playing === "all" ? "Stop" : "Listen"}
          </button>
          <button
            data-testid="copy-all-button"
            onClick={copyAll}
            className="flex items-center gap-2 px-4 py-2.5 text-sm border border-[#D9D4CC] text-[#2C2A29] hover:bg-[#EAE6DF] transition-colors duration-300 rounded-sm"
          >
            {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
            Copy all
          </button>
          <ShareMenu payload={setPayload} label="Share" variant="primary" testId="share-set" />
        </div>
      </div>

      <WordStudy studies={session.word_study} />

      <ol className="space-y-7">
        {session.decrees.map((d, i) => (
          <li
            key={i}
            data-testid={`decree-item-${i + 1}`}
            className={`group flex gap-4 sm:gap-5 border-b border-[#EAE6DF] pb-7 last:border-0 transition-colors duration-300 ${activeIdx === i ? "bg-[#F3F0EA] -mx-3 px-3 rounded-sm" : ""}`}
          >
            <span className="font-heading text-2xl sm:text-3xl text-[#B7AE9F] leading-none pt-1 select-none w-9 shrink-0">
              {i + 1}
            </span>
            <div className="flex-1">
              <p className="font-decree text-lg sm:text-xl leading-relaxed font-semibold text-[#2C2A29]">
                {d.text}
              </p>
              <p className="font-body text-sm italic text-[#7A756E] mt-2">({d.reference})</p>
            </div>
            <div className="flex flex-col gap-2 self-start">
              <button
                data-testid={`play-decree-${i + 1}`}
                onClick={() => playOne(i)}
                aria-label={`Listen to decree ${i + 1}`}
                className={`transition-opacity duration-300 p-1 ${activeIdx === i ? "text-[#2C2A29]" : "text-[#B7AE9F] hover:text-[#2C2A29] opacity-100 sm:opacity-0 sm:group-hover:opacity-100"}`}
              >
                {loadingIdx === i ? <Loader2 className="w-4 h-4 animate-spin" /> : (playing === i || (playing === "all" && activeIdx === i)) ? <Square className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
              </button>
              <button
                data-testid={`copy-decree-${i + 1}`}
                onClick={() => copyOne(d, i)}
                aria-label={`Copy decree ${i + 1}`}
                className="opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity duration-300 text-[#B7AE9F] hover:text-[#2C2A29] p-1"
              >
                <Copy className="w-4 h-4" />
              </button>
              <div className="opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity duration-300">
                <ShareMenu
                  payload={{ kind: "single", topic: session.topic, decrees: [{ text: d.text, reference: d.reference }] }}
                  iconOnly
                  label={`Share decree ${i + 1}`}
                  testId={`share-decree-${i + 1}`}
                />
              </div>
            </div>
          </li>
        ))}
      </ol>

      <ScripturesReferenced decrees={session.decrees} />
    </div>
  );
}
